import {Injectable} from "@angular/core";
import {ThemeProxy} from "./theme.proxy";
import cssVars from "css-vars-ponyfill";
import {BehaviorSubject, Observable, of} from "rxjs";
import {map} from "rxjs/operators";

interface PatientPortalTheme {
	commonThemeMap: { [key: string]: string };
	factoryColorGroupToColorMap: { [key: string]: string };
	customColorGroupToColorMap: { [key: string]: string };
}

export type PatientPortalThemeType = "factory" | "custom";

/**
 ****************************************************************
 * Service class theme management.
 *
 * Created by Shai Zelman on 06/06/2020.
 ****************************************************************
 */
@Injectable({
	providedIn: "root"
})
export class ThemeService {
	private theme: PatientPortalTheme = null;

	private currentThemeSubject: BehaviorSubject<PatientPortalThemeType> = new BehaviorSubject<PatientPortalThemeType>("custom");

	public isCustomThemeAvailable: boolean = false;
	public isFactoryThemeAvailable: boolean = false;

	constructor(private themeProxy: ThemeProxy) {

	}

	public get currentTheme() {
		return this.currentThemeSubject.asObservable();
	}

	public getTheme(): Observable<any> {
		// If the theme was already loaded, just return an empty Observable immediately to trigger any callbacks that wait for the theme
		if (this.theme) {
			return of(null);
		}

		return this.themeProxy.getTheme().pipe(map((theme: PatientPortalTheme) => {
			this.theme = theme;

			this.isCustomThemeAvailable = this.theme?.customColorGroupToColorMap !== null;
			this.isCustomThemeAvailable ? this.setCustomTheme() : this.setFactoryTheme();

			this.isFactoryThemeAvailable = this.theme?.factoryColorGroupToColorMap !== null;
		}));
	}

	public toggleTheme() {
		if (this.currentThemeSubject.value === "custom") {
			this.currentThemeSubject.next("factory");
			window.sessionStorage.setItem("dynamicTheme", "factory");
			this.setFactoryTheme();
		} else {
			this.currentThemeSubject.next("custom");
			window.sessionStorage.setItem("dynamicTheme", "custom");
			this.setCustomTheme();
		}
	}

	public setFactoryTheme() {
		this.setTheme(this.factoryTheme);
	}

	public setCustomTheme() {
		this.setTheme(this.customTheme);
	}

	private setTheme(colorMap) {
		cssVars({
			variables: colorMap,
			watch: true,
			include: "style"
		});
	}

	private get customTheme(): { [key: string]: string } {
		return !this.theme ? {} : {
			...this.theme.commonThemeMap,
			...this.theme.customColorGroupToColorMap
		};
	}

	private get factoryTheme(): { [key: string]: string } {
		return !this.theme ? {} : {
			...this.theme.commonThemeMap,
			...this.theme.factoryColorGroupToColorMap
		};
	}
}
